/**
 * tm/todoCheckView.ts — 일일 업무 「결과 확인」 흐름에서 지금 누구 차례인지 (PC 카드 · 휴대폰 공용 · 2026-10-22)
 *  서버 규칙은 backend/services/todoCheck.js — 여기는 화면에 무엇을 보일지만 정한다.
 */
import type { Todo } from './types';

export type CheckStage =
  | 'plain'          // 결과 확인을 받지 않는 일(지금처럼)
  | 'write'          // 담당자: 알아보고 결과 적기
  | 'waitCheck'      // 담당자: 지도사 확인 기다림
  | 'staffNotify'    // 담당자: 안내할 차례
  | 'bossNotifies'   // 담당자: 지도사가 안내하기로 함
  | 'waitResult'     // 맡긴 사람: 직원이 알아보는 중
  | 'check'          // 맡긴 사람: 결과 확인할 차례
  | 'bossNotify'     // 맡긴 사람: 내가 안내할 차례
  | 'staffNotifying' // 맡긴 사람: 직원이 안내하는 중
  | 'done';

export const RESULT_MIN = 10;
/** 서버와 같은 셈 — 빈칸 · 문장부호 빼고 글자 수 */
export function realLen(s: string): number { return String(s || '').replace(/[\s.,·!?~\-_()[\]'"]/g, '').length; }

export function stageOf(t: Todo, myId: string): CheckStage {
  if (!t.needs_check) return 'plain';
  if (t.status === 'done') return 'done';
  const creator = t.created_by === myId;
  const assignee = Array.isArray(t.assignees) && t.assignees.includes(myId);
  if (creator) {
    if (t.status === 'review') return 'check';
    if (t.status === 'notify') return t.notify_by === 'me' ? 'bossNotify' : 'staffNotifying';
    return 'waitResult';
  }
  if (assignee) {
    if (t.status === 'review') return 'waitCheck';
    if (t.status === 'notify') return t.notify_by === 'staff' ? 'staffNotify' : 'bossNotifies';
    return 'write';
  }
  return 'plain';
}

/** 전화 · 문자에 쓸 숫자만 */
export function telDigits(p: string | null | undefined): string { return String(p || '').replace(/[^\d+]/g, ''); }

/** 맡은 사람 이름(맡긴 사람 빼고) */
export function staffNames(t: Todo, staff: { id: string; name: string }[]): string {
  const ids = (Array.isArray(t.assignees) ? t.assignees : []).filter(id => id && id !== t.created_by);
  return ids.map(id => (staff.find(s => s.id === id) || { name: '' }).name).filter(Boolean).join(' · ') || '담당자';
}

/** 다시 볼 날 고르기 — 내일 · 모레 · 3일 뒤 · 일주일 뒤(토 · 일이면 월요일로) */
export function waitChoices(today: string): { label: string; date: string }[] {
  const add = (n: number) => {
    const t = new Date(Date.parse(today + 'T00:00:00Z') + n * 86400000);
    const w = t.getUTCDay();
    if (w === 6) t.setUTCDate(t.getUTCDate() + 2); else if (w === 0) t.setUTCDate(t.getUTCDate() + 1);
    return t.toISOString().slice(0, 10);
  };
  return [{ label: '내일', date: add(1) }, { label: '모레', date: add(2) }, { label: '3일 뒤', date: add(3) }, { label: '일주일 뒤', date: add(7) }];
}
/** 오늘(한국 날짜) */
export function kstTodayStr(now: number = Date.now()): string { return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10); }
/** 「10/24」 */
export function mdOf(d: string | null | undefined): string { const m = String(d || '').match(/^\d{4}-(\d{2})-(\d{2})/); return m ? `${+m[1]}/${+m[2]}` : ''; }
/** 답 기다리는 중이면 그 글(진행 메모) · 아니면 '' */
export function waitingNote(t: Todo, now: number = Date.now()): string {
  const s = t.snoozed_until ? Date.parse(t.snoozed_until) : NaN;
  return !isNaN(s) && s > now && t.progress_note ? t.progress_note : '';
}
/** 글 복사 — 안 되는 브라우저는 숨은 칸으로 */
export async function copyText(s: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(s); return true; } catch { /* 아래 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = s; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}
